/**
 * Chat composer drafts (DOP-267)
 *
 * A 401 tears the page down mid-action, so the composer text is mirrored into
 * sessionStorage. Two slots per chat:
 *
 * - the DRAFT slot holds what is currently typed,
 * - the PENDING slot holds a message that was submitted but not yet accepted by
 *   the backend. `sendMessage` empties the composer before the chat-create /
 *   stream-start round trip, so without this slot a 401 at submit time would
 *   destroy the text the user just sent.
 *
 * Staging MOVES the text from draft to pending, so the same message never sits
 * in both slots and cannot be resurrected after it was consumed. The pending
 * slot is dropped only once the backend has accepted the message.
 *
 * Every key is namespaced by the authenticated user id: sessionStorage outlives
 * a logout, so without that namespace the next person to log in in the same tab
 * could restore the previous user's composer text. Identity changes and explicit
 * logouts also wipe every stored draft (`rememberChatDraftOwner`).
 */

const DRAFT_PREFIX = "doppler.chat_draft:";
const PENDING_SUFFIX = ":pending";
const OWNER_KEY = "doppler.chat_draft_owner";

export const chatDraftKey = (
	chatId: string | null | undefined,
	userId: string | null | undefined,
): string =>
	`${DRAFT_PREFIX}${encodeURIComponent(userId ?? "anonymous")}:${encodeURIComponent(
		chatId ?? "new",
	)}`;

const pendingKeyFor = (key: string): string => `${key}${PENDING_SUFFIX}`;

/**
 * Submissions this PAGE SESSION has in flight. While a send is live, a route
 * change from /chat/new to /chat/<id> must not spill the in-flight message back
 * into the composer. A hard redirect (the 401 case) reloads the page and empties
 * this set, which is exactly when the pending text should be recovered.
 */
const inFlight = new Set<string>();

/** Test seam: simulates the page reload that a 401 redirect causes. */
export const resetChatDraftPageSession = (): void => {
	inFlight.clear();
};

// sessionStorage throws in some private-browsing modes. A lost draft is never
// worth breaking the chat over, so every access is best-effort.
const getItem = (key: string): string | null => {
	try {
		return window.sessionStorage.getItem(key);
	} catch {
		return null;
	}
};

const setItem = (key: string, value: string): void => {
	try {
		window.sessionStorage.setItem(key, value);
	} catch {
		/* best effort */
	}
};

const removeItem = (key: string): void => {
	try {
		window.sessionStorage.removeItem(key);
	} catch {
		/* best effort */
	}
};

/** Persist (or clear) the live composer text for a chat. */
export const writeChatDraft = (key: string, value: string): void => {
	if (value.trim()) {
		setItem(key, value);
	} else {
		removeItem(key);
	}
};

/**
 * Read the draft for a chat, promoting an unaccepted submission back into the
 * draft slot when the composer is otherwise empty and the send is not still
 * running in this page session. Reading IS the restore: the pending slot is
 * consumed so the message cannot come back a second time.
 */
export const readChatDraft = (key: string): string => {
	const pendingKey = pendingKeyFor(key);
	const draft = getItem(key);
	if (draft) {
		// A pending copy of the very text that is already sitting in the draft
		// slot is spent — drop it rather than leaving it to resurface later.
		if (getItem(pendingKey) === draft) removeItem(pendingKey);
		return draft;
	}

	if (inFlight.has(key)) return "";

	const pending = getItem(pendingKey);
	removeItem(pendingKey);
	if (!pending) return "";
	setItem(key, pending);
	return pending;
};

/**
 * Called at submit time, before the composer is emptied. Moves the text out of
 * the draft slot so the two slots can never hold the same message.
 */
export const stageChatDraftSubmission = (key: string, text: string): void => {
	if (!text.trim()) return;
	setItem(pendingKeyFor(key), text);
	removeItem(key);
	inFlight.add(key);
};

/**
 * A chat id minted mid-send changes the key the user will return to, so the
 * pending record has to follow it — immediately, not in the failure path: a 401
 * can tear the page down before any catch block runs.
 */
export const migrateChatDraftSubmission = (
	fromKey: string,
	toKey: string,
): void => {
	if (fromKey === toKey) return;
	const pending = getItem(pendingKeyFor(fromKey));
	removeItem(pendingKeyFor(fromKey));
	if (inFlight.delete(fromKey)) inFlight.add(toKey);
	if (!pending) return;
	setItem(pendingKeyFor(toKey), pending);
};

/** Called once the backend has accepted the message — the text is safe to drop. */
export const confirmChatDraftSubmission = (key: string): void => {
	removeItem(pendingKeyFor(key));
	inFlight.delete(key);
};

/**
 * Called when the send failed (an expired session, a network drop): move the
 * text back into the draft slot so it is waiting after re-login. If the user
 * has already typed something new, that newer text wins and the submission is
 * dropped rather than silently overwriting it.
 */
export const restoreChatDraftSubmission = (key: string): string | null => {
	const pending = getItem(pendingKeyFor(key));
	removeItem(pendingKeyFor(key));
	inFlight.delete(key);
	if (!pending) return null;
	if (getItem(key)) return null;
	setItem(key, pending);
	return pending;
};

/** Wipe every stored draft and pending record, for any chat and any user. */
export const clearAllChatDrafts = (): void => {
	inFlight.clear();
	try {
		const doomed: string[] = [];
		for (let i = 0; i < window.sessionStorage.length; i += 1) {
			const key = window.sessionStorage.key(i);
			if (key?.startsWith(DRAFT_PREFIX)) doomed.push(key);
		}
		doomed.forEach((key) => window.sessionStorage.removeItem(key));
	} catch {
		/* best effort */
	}
};

/**
 * Bind stored drafts to whoever is signed in. Called with the user id on login
 * and on session restore, and with `null` on logout. A different principal (or
 * no principal) drops every stored draft, so nobody inherits the composer text
 * of the previous person to use this tab.
 */
export const rememberChatDraftOwner = (userId: string | null): void => {
	const previous = getItem(OWNER_KEY);
	if (!userId) {
		clearAllChatDrafts();
		removeItem(OWNER_KEY);
		return;
	}
	if (previous !== userId) {
		clearAllChatDrafts();
		setItem(OWNER_KEY, userId);
	}
};

export interface ChatDraftRecoveryOptions {
	/**
	 * True only for a 401 from an expired session. Then the request never
	 * authenticated, so the backend cannot have committed the turn and the text
	 * is restored unconditionally.
	 */
	sessionExpired: boolean;
	/** The submitted text, as it would appear in history. */
	message: string;
	/**
	 * Refetches the chat's LATEST user turn from the server, or null when the
	 * chat has none. Omitted when there is no chat to ask about — e.g. the
	 * failure happened before an id existed.
	 */
	fetchLatestUserTurn?: () => Promise<string | null>;
}

const sameTurnText = (a: string, b: string): boolean => a.trim() === b.trim();

/**
 * Decide whether a failed send's text goes back into the composer.
 *
 * The backend commits the user message and the generation BEFORE enqueueing, and
 * can answer 503 after the enqueue fails — and a committed response can be lost
 * in transit. Restoring on every failure would therefore put text back that is
 * already a turn in history, which the user then sends twice (and pays for
 * twice, when it kicks off a workflow).
 *
 * So: a session-expiry 401 restores outright; any other failure reconciles
 * against the server's own history first, and only restores when the turn is
 * demonstrably absent — or when the reconciliation itself could not be done,
 * since a duplicate is a lesser harm than silently eating the user's message.
 */
export const reconcileChatDraftSubmission = async (
	key: string,
	{ sessionExpired, message, fetchLatestUserTurn }: ChatDraftRecoveryOptions,
): Promise<string | null> => {
	if (sessionExpired || !fetchLatestUserTurn) {
		return restoreChatDraftSubmission(key);
	}

	try {
		const latest = await fetchLatestUserTurn();
		// Only the LATEST turn counts: the same words sent earlier in the
		// conversation say nothing about whether THIS submission landed.
		if (latest !== null && sameTurnText(latest, message)) {
			// The turn landed despite the error: drop the copy and let the normal
			// error surface. Restoring here would invite a duplicate send.
			confirmChatDraftSubmission(key);
			return null;
		}
	} catch {
		// Could not ask the server — fall through and restore.
	}

	return restoreChatDraftSubmission(key);
};

export interface ChatDraftSyncState {
	/** null before the first sync — the mount case. */
	key: string | null;
	value: string;
}

export interface ChatDraftSyncResult {
	state: ChatDraftSyncState;
	/**
	 * Non-null when the chat key changed and the composer must be loaded with
	 * this chat's own draft. null means "nothing to load, text was persisted".
	 */
	restored: string | null;
}

/**
 * One step of composer↔storage sync.
 *
 * The key changes when the route moves between chats while the component stays
 * mounted. At that moment the composer still holds the DEPARTING chat's text,
 * so it must be filed under the old key — otherwise chat A's text lands under
 * chat B and shadows B's own draft.
 */
export const syncChatDraftForKey = (
	previous: ChatDraftSyncState,
	nextKey: string,
	currentInput: string,
): ChatDraftSyncResult => {
	if (previous.key === nextKey) {
		writeChatDraft(nextKey, currentInput);
		return { state: { key: nextKey, value: currentInput }, restored: null };
	}

	if (previous.key !== null) {
		writeChatDraft(previous.key, previous.value);
	}
	const restored = readChatDraft(nextKey);
	return { state: { key: nextKey, value: restored }, restored };
};
